"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { useWaveDropsLeaderboard } from "@/hooks/useWaveDropsLeaderboard";
import React from "react";
import { WaveLeaderboardGridItem } from "./WaveLeaderboardGridItem";
export type WaveLeaderboardGridMode = "compact" | "content_only";
interface WaveLeaderboardGridProps {
  readonly wave: ApiWave;
  readonly sort: WaveDropsLeaderboardSort;
  readonly mode: WaveLeaderboardGridMode;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly curatedByGroupId?: string | undefined;
  readonly minPrice?: number | undefined;
  readonly maxPrice?: number | undefined;
  readonly priceCurrency?: string | undefined;
}
export const WaveLeaderboardGrid: React.FC<WaveLeaderboardGridProps> = t0 => {
  const $ = _c(31);
  const {
    wave,
    sort,
    mode,
    isVotingClosed: t1,
    isVotingControlsLocked: t2,
    onDropClick,
    curatedByGroupId,
    minPrice,
    maxPrice,
    priceCurrency
  } = t0;
  const isVotingClosed = t1 === undefined ? false : t1;
  const isVotingControlsLocked = t2 === undefined ? false : t2;
  const winningThreshold = wave.wave.type === ApiWaveType.Approve ? wave.wave.winning_threshold : null;
  const winningThresholdMinDurationMs = wave.wave.type === ApiWaveType.Approve ? wave.wave.winning_threshold_min_duration_ms : null;
  let t3;
  if ($[0] !== curatedByGroupId || $[1] !== maxPrice || $[2] !== minPrice || $[3] !== priceCurrency || $[4] !== sort || $[5] !== wave.id) {
    t3 = {
      waveId: wave.id,
      sort,
      curatedByGroupId,
      minPrice,
      maxPrice,
      priceCurrency
    };
    $[0] = curatedByGroupId;
    $[1] = maxPrice;
    $[2] = minPrice;
    $[3] = priceCurrency;
    $[4] = sort;
    $[5] = wave.id;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage
  } = useWaveDropsLeaderboard(t3);
  if (isFetching && drops.length === 0) {
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-@container" role="status" aria-label="Loading drops" aria-busy="true"><div className="tw-grid tw-gap-4 @lg:tw-grid-cols-2 @3xl:tw-grid-cols-3">{Array.from({
            length: 6
          }).map(_temp)}</div></div>;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  if (drops.length === 0) {
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-flex tw-h-32 tw-items-center tw-justify-center tw-text-sm tw-text-iron-500">No drops to show</div>;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  let t4;
  if ($[9] !== drops || $[10] !== isVotingClosed || $[11] !== isVotingControlsLocked || $[12] !== mode || $[13] !== onDropClick || $[14] !== winningThreshold || $[15] !== winningThresholdMinDurationMs) {
    let t5;
    if ($[17] !== isVotingClosed || $[18] !== isVotingControlsLocked || $[19] !== mode || $[20] !== onDropClick || $[21] !== winningThreshold || $[22] !== winningThresholdMinDurationMs) {
      t5 = drop => <WaveLeaderboardGridItem key={drop.id} drop={drop} mode={mode} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} onDropClick={onDropClick} />;
      $[17] = isVotingClosed;
      $[18] = isVotingControlsLocked;
      $[19] = mode;
      $[20] = onDropClick;
      $[21] = winningThreshold;
      $[22] = winningThresholdMinDurationMs;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    t4 = drops.map(t5);
    $[9] = drops;
    $[10] = isVotingClosed;
    $[11] = isVotingControlsLocked;
    $[12] = mode;
    $[13] = onDropClick;
    $[14] = winningThreshold;
    $[15] = winningThresholdMinDurationMs;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[24] !== fetchNextPage || $[25] !== hasNextPage || $[26] !== isFetchingNextPage) {
    t5 = hasNextPage && <div className="tw-col-span-full tw-mb-2 tw-mt-4 tw-flex tw-justify-center"><button onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-text-iron-400 tw-transition desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-300">{isFetchingNextPage ? "Loading more..." : "Load more drops"}</button></div>;
    $[24] = fetchNextPage;
    $[25] = hasNextPage;
    $[26] = isFetchingNextPage;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== t4 || $[29] !== t5) {
    t6 = <div className="tw-@container"><div className="tw-grid tw-gap-4 @lg:tw-grid-cols-2 @3xl:tw-grid-cols-3">{t4}{t5}</div></div>;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  return t6;
};
function _temp(_, i) {
  return <div key={i} className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800/60 tw-bg-iron-950"><div className="tw-aspect-square tw-min-h-[14rem] tw-animate-pulse tw-bg-iron-900/80 md:tw-min-h-[15rem]" /><div className="tw-space-y-2 tw-px-3 tw-py-3"><div className="tw-h-4 tw-w-3/4 tw-animate-pulse tw-rounded tw-bg-iron-800/50" /><div className="tw-h-3 tw-w-1/3 tw-animate-pulse tw-rounded tw-bg-iron-800/40" /></div></div>;
}